import { has, get, isPlainObject, isEmpty, camelCase } from 'lodash';

import getOverrides from 'APP_ROOT/utils/get-field-overrides';
import applyReadOnlyTransform from 'APP_ROOT/utils/applyReadOnlyTransform';
import { getValueFromPopulatedSource } from 'APP_ROOT/utils/conditionals';
import getPresenceUsage from 'APP_ROOT/utils/getPresenceUsage';
import { formatInchesToFtInch } from 'APP_ROOT/utils/units-transformation';
import getGroupData from './getGroupData';
import getIsReadOnly from './getIsReadOnly';
import mapOfficerNames from './mapOfficerNames';
import { DATE } from '../../../../constants/fieldTypes';
import {
  momentWithTZ,
  BENCHMARK_DATE_FORMAT,
} from '../../../../utils/parse-date';
import { DISPLAY_FROM } from 'APP_ROOT/modules/FormBuilder/constants/constants';
import {
  EXTERNAL_USERS,
  EXTERNAL_USERS_LIST,
} from 'APP_ROOT/modules/FormBuilder/constants/fieldAutocomplete';
import getAutocompleteSource from '../../field-types/utils/getAutocompleteSource';

const getCalculateValueFromSource = ({
  fromSource,
  data,
  groupData,
  type,
  isReadOnly,
  form,
  dataKey,
}) => {
  const {
    source = '',
    key = '',
    field = '',
    keyDependsOn,
    valueToKey = {},
    fieldFromRoot = false,
  } = fromSource;

  // OACC-37/OACC-33: external users (JMS Inmate / External Staff Member) are
  // stored nested under data.__externalUsers.__<source>, unlike officer/civilian
  // (`users`) collections which live at data.__<source>. Mirror the display-path
  // branch in utils/conditionals.js (getValueFromPopulatedSource).
  const sourceCollectionKey = `__${source}`;
  const isExternalSource = EXTERNAL_USERS_LIST.includes(sourceCollectionKey);
  const sourceData = isExternalSource
    ? get(data, [EXTERNAL_USERS, sourceCollectionKey], {})
    : get(data, sourceCollectionKey, {});
  const { [field]: selection = '' } = fieldFromRoot ? data : groupData;
  let { [selection]: selectionData = {} } = sourceData;

  if (has(selectionData, 'height')) {
    selectionData = {
      ...selectionData,
      ...formatInchesToFtInch(selectionData.height),
    };
  }

  let sourceKey = key;

  if (keyDependsOn) {
    const keyDependsOnValue = groupData[keyDependsOn];
    sourceKey = valueToKey[keyDependsOnValue];
  }

  let value = null;

  if (isExternalSource) {
    // External-user records (JMS Inmate / External Staff Member) use camelCase
    // keys, while the configured profile keys are snake_case. Map the key, then
    // look it up at the top level and fall back to typeSpecificAttributes
    // (nested design-doc shape vs. flattened raw shape). Use ?? (not ||) so a
    // legitimately false/empty-string value is preserved.
    const typeSpecificAttributes = get(selectionData, 'typeSpecificAttributes');

    if (Array.isArray(sourceKey)) {
      value = sourceKey
        .map(item => {
          const camelKey = camelCase(item);
          return (
            get(selectionData, camelKey) ??
            get(typeSpecificAttributes, camelKey) ??
            ''
          );
        })
        .join(' ');
    } else {
      const camelKey = camelCase(sourceKey);
      value =
        get(selectionData, camelKey) ??
        get(typeSpecificAttributes, camelKey) ??
        null;
    }
  } else if (Array.isArray(sourceKey)) {
    value = key.map(item => get(selectionData, item, '')).join(' ');
  } else {
    value = get(selectionData, sourceKey, null);
  }

  // Read-only DATE fields format to BENCHMARK_DATE_FORMAT with no timezone
  // conversion (useTimezone=false). This applies to both officer (`users`)
  // dates and external JMS dates — JMS dates are plain ISO calendar dates
  // (e.g. "1972-07-24"), so skipping .tz() avoids an off-by-one day shift.
  if (type === DATE && isReadOnly && (source === 'users' || isExternalSource)) {
    const fieldValue = value;
    if (!isEmpty(fieldValue)) {
      const formattedDate = momentWithTZ(
        fieldValue,
        undefined,
        undefined,
        false
      );
      value = formattedDate.format(BENCHMARK_DATE_FORMAT);
    }
  }
  return value;
};

const getCalculatedValue = props => {
  const {
    data = {},
    dataEnums,
    dataKey: _dataKey = '',
    field_type: type = '',
    overrides = {},
    parentKey = '',
    parentIndex = 0,
    mainIndex = 0,
    isReviewer = false,
    fromSource = null,
    dependsOn,
    loopFromEnumRef,
    form,
    isContributeReport,
    contributorAssignmentCanEdit,
  } = props;

  const getFieldOverrides = getOverrides(props, overrides);
  const options = getFieldOverrides('options', {});

  const {
    populateFrom = '',
    multiselect: canSelectMultiple,
    presenceUsageFrom,
    presenceUsageValue,
    presenceUsageFilterBy,
    showPresenceUsage,
    [DISPLAY_FROM]: displayFrom = '',
  } = options;
  const source = getAutocompleteSource({
    options: { ...options, settings: props.settings },
  });

  const displayFromKey = displayFrom.split('.');
  let dataKey;
  if (displayFromKey.length > 1 && parentKey) {
    if (displayFromKey[0] === parentKey) {
      dataKey = `${parentKey}.${parentIndex}.${displayFromKey[1]}`;
    } else {
      dataKey = `${displayFromKey[0]}.${mainIndex}.${displayFromKey[1]}`;
    }
  } else {
    dataKey = displayFrom ? displayFrom : _dataKey;
  }

  const groupData = displayFrom ? data : getGroupData(props);
  const contributeReadOnly =
    isContributeReport && !contributorAssignmentCanEdit;
  const isReadOnly = getIsReadOnly(props) || contributeReadOnly;

  let calculatedValue = get(groupData, dataKey);

  const isDynamicallyPopulated = isReadOnly && populateFrom;
  const hasReadOnlyTransform = has(options, 'readOnlyTransform');

  if (dependsOn && hasReadOnlyTransform) {
    const { readOnlyTransform } = options;
    calculatedValue = get(groupData, dependsOn);

    if (isPlainObject(readOnlyTransform)) {
      calculatedValue = applyReadOnlyTransform(
        calculatedValue,
        readOnlyTransform
      );
    }
  }

  if (fromSource) {
    calculatedValue = getCalculateValueFromSource({
      fromSource,
      data,
      groupData,
      type,
      isReadOnly,
      form,
      dataKey,
    });
  } else if (isDynamicallyPopulated) {
    const sourceKey = source.startsWith('__') ? source : `__${source}`;
    const sources = Object.keys(populateFrom);
    const fromField = get(options, 'populateFromField', null);

    const valueFromPopulatedSource = getValueFromPopulatedSource(
      sources,
      populateFrom,
      dataKey,
      data,
      groupData,
      fromField,
      sourceKey
    );

    calculatedValue = valueFromPopulatedSource;
  } else if (
    type === 'autocomplete' &&
    canSelectMultiple &&
    Array.isArray(calculatedValue) &&
    isReviewer
  ) {
    const sourceKey = source.startsWith('__') ? source : `__${source}`;
    const sourceData = get(data, sourceKey);

    calculatedValue = mapOfficerNames(calculatedValue, sourceData);
  } else if (type === 'autocomplete' && isReviewer) {
    const { tab } = props;
    const fields = get(props, 'presentation.fields', []);
    const data = fields[tab] || {};
    calculatedValue = get(data, dataKey, null);
  }

  if (showPresenceUsage) {
    calculatedValue = getPresenceUsage({
      dataKey,
      parentIndex,
      loopFromEnumRef,
      data,
      dataEnums,
      presenceUsageFrom,
      presenceUsageValue,
      presenceUsageFilterBy,
      showPresenceUsage,
    });
  }

  return calculatedValue;
};

export default getCalculatedValue;
